/**
 * Project page of DaaS application
 * opens when project is selected from Dashboard page
 * Created Date: 18/06/2024
 */
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

/**
 * Motif controls
 */
import MotifButton, {
  MotifButtonGroup,
  MotifIconButton,
} from "@ey-xd/motif-react/Button";
import MotifTable from "@ey-xd/motif-react/Table";
import MotifToast from "@ey-xd/motif-react/Toast";
import { MotifTooltip } from "@ey-xd/motif-react";
import MotifDropdown, { MotifDropdownItem } from "@ey-xd/motif-react/Dropdown";

//  Icons imports
import iconArrowBack from "../../../assets/images/arrow_back.svg";
import iconAdd from "../../../assets/images/add-circle-white.svg";
import iconThresholds from "../../../assets/images/ease-curve-control-points.svg";
import iconMore from "../../../assets/images/more-horiz.svg";
import iconRelationship from "../../../assets/images/link.svg";
import iconEditTable from "../../../assets/images/edit-table.svg";
import iconDeleteTable from "../../../assets/images/delete-table.svg";
import iconError from "../../../assets/images/error.svg";
import iconMembers from "../../../assets/images/iconMembers.svg";
import iconDocuments from "../../../assets/images/iconDocuments.svg";
import iconWhiteCheckCircle from "../../../assets/images/white-check-circle.svg";
import iconCheckCircle from "../../../assets/images/check-circle.svg";
import iconWarningError from "../../../assets/images/warning_error.svg";
import iconLineCircleRed from "../../../assets/images/line_circle_red.svg";
import iconRefresh from "../../../assets/images/refresh.svg";
import iconLoader from "../../../assets/images/loader.svg";
import unpublished from '../../../assets/images/unpublished.svg';
// import published from '../../../assets/images/published.svg';
//  Redux imports
import {
  setUserProjects,
  setActiveProject,
  setActiveProjectUserRole,
} from "../../../redux/userSlice";
import { setTables, setSelectedTable } from "../../../redux/tableSlice";

/**
 * Internal imports
 */
import "./ProjectDetails.scss";
import Utils from "../../../utils/Utils";
import DocumentList from "../../molecules/modals/projectDetails/DocumentList";
import TableService from "../../../services/tableService";
import AddTable from "../../molecules/modals/addTable/AddTable";
import ProjectMembers from "../../molecules/modals/projectDetails/ProjectMembers";
import TableDetails from "../tableDetails/TableDetails";
import Thresholds from "../../molecules/modals/thresholds/Thresholds";
import DeleteTableModal from "../../molecules/modals/deleteTable/DeleteTableModal";

const ProjectDetails = () => {
 const { tableTypes }  = useSelector((state) => state.prompt);
  /**
   * Custom constants
   */
  const vTabIds = {
    tables: "idTabContentTables",
    users: "idTabContentUsers",
    documents: "idTabContentDocuments",
  };
  const noRowsTemplate = `<span class="ag-overlay-no-rows-center">To get started, select "Add Table" below.</span>`;
  const [colDefs] = useState([
    {
      field: "tableName",
      headerName: "Name",
      wrapText: true,
      flex: 2,
      autoHeight: true,
      cellRenderer: (params) => params.data.tableName,
      filter: false,
      sortable: false,
    },
    {
      field: "metadata.uploadRange",
      headerName: "Range",
      wrapText: true,
      flex: 2,
      autoHeight: true,
      cellRenderer: (params) => (  
        <div>
          {`'${params.data.metadata.sheetName}'!${params.data.metadata.uploadRange}`}
        </div>
      ),
      filter: false,
      sortable: false,
    },
    {
      field: "tableStatus",
      headerName: "Status",
      wrapText: true,
      width: 100,
      autoHeight: true,
      cellRenderer: (params) => {
        const tableType = params.data.tableType;
        const matchedTableType = tableTypes?.find(
          (item) => item.api === tableType
        );
        if (activeProject.category !== "3" && matchedTableType && !matchedTableType.isPublished) {
          return (
            <MotifTooltip
              trigger={
                <span className="icon-offset">
                  <img src={unpublished} alt="Unpublished" />
                </span>
              }
              placement="bottom"
              hideCloseButton={true}
              allowClick={true}
              contentClassName="tooltipleft"          
            >
             Table Type has been unpublished.<br /> Edit Table Type to update analysis
            </MotifTooltip>
          );
        }
        const STATUS_MAP = {
          1: {
            label: "Error in generating analysis",
            icon: (
              <img src={iconWarningError} alt="error" className="status-icon" />
            ),
            tooltip: true,
          },
          2: {
            label: "Analysis in progress",
            icon: (
              <img src={iconLoader} alt="Analysis in progress" className="loader" />
            ),
            tooltip: true,
          },
          3: {
            label: "Change in analysis settings",
            icon: (
              <img
                src={iconLineCircleRed}
                alt="Change in analysis settings"
                className="status-icon-completed"
              />
            ),
            tooltip: true,
          },
          4: {
            label: "Completed",
            icon: (
              <img
                src={iconCheckCircle}
                alt="Completed"
                className="status-icon-completed"
              />
            ),
            tooltip: false,
          },
          5: {
            label: null,
            icon: null,
          },
        };

        const status = STATUS_MAP[params.data.ProjectLevelTable_status];
        if (status) {
          if (status.tooltip && status.icon) {
            return (
              <MotifTooltip
                trigger={status.icon}
                allowClick={false}
                placement="left"
                hideCloseButton={true}
              >
                {status.label}
              </MotifTooltip>
            );
          } else if (status.icon) {
            return status.icon;
          } else {
            return null;
          }
        } else {
          return null;
        }
      },
      filter: false,
      sortable: false,
    },
    {
      field: "",
      headerName: "Actions",
      colId: "actions",
      wrapText: true,
      width: 100,
      autoHeight: true,
      cellRenderer: (params) => (
        <div>
          {params.data.tableRelationships?.length > 0 && (
            <MotifTooltip
              trigger={<img src={iconRelationship} alt="Relationship" />}
              placement="left"
              hideCloseButton={true}
            >
              existing relationship(s) : {params.data.tableRelationships.length}
            </MotifTooltip>
          )}
          <MotifDropdown
            aria-labelledby={"more-dropdown-trigger-" + params.data.id}
            className="edit-table-dropdown"
            handleClickOutside={() =>
              toggleDropdownMenuMore(params.data.id, "Close")
            }
            id={"more-dropdown-" + params.data.id}
            placement="bottom-right"
            trigger={
              <MotifIconButton
                aria-label="More"
                id={"more-dropdown-trigger-" + params.data.id}
                onClick={() => toggleDropdownMenuMore(params.data.id, "Open")}
                disabled={params.data.LAInProc}
                className="tableBtnMore"
              >
                <MotifTooltip
                  hide={!params.data.LAInProc}
                  trigger={
                    <img className="tableIconMore" src={iconMore} alt="More" />
                  }
                  placement="left"
                  allowHover={true}
                  allowClick={false}
                  hideCloseButton={true}
                  contentClassName="tooltipWithoutClose"
                >
                  Analysis in progress
                </MotifTooltip>
              </MotifIconButton>
            }
          >
            <span className="document-download-delete">
              {(() => {
                return (
                  <>
                    <MotifDropdownItem
                      onClick={() => onEditClick(params.data)}
                      data
                    >
                      <img
                        src={iconEditTable}
                        alt="Edit Table"
                        className="iconedit"
                      />{" "}
                      Edit Table
                    </MotifDropdownItem>
                    <MotifDropdownItem
                      onClick={() => onDeleteClick(params.data)}
                      data
                    >
                      <img
                        src={iconDeleteTable}
                        alt="Delete Table"
                        className="icondelete"
                      />{" "}
                      Delete Table
                    </MotifDropdownItem>
                  </>
                );
              })()}
            </span>
          </MotifDropdown>
        </div>
      ),
      filter: false,
      sortable: false,
    },
  ]);

  const isUnpublishedTableType = (tableType) => {
    const matched = tableTypes.find(
      (item) =>
        item.api=== tableType
    );
    return matched ? !matched.isPublished : false;
  };

  const toggleDropdownMenuMore = (vDropdownMenuId, vToggle) => {
    const vDropdown = document.getElementById(
      "more-dropdown-" + vDropdownMenuId
    );
    if (vDropdown && vToggle === "Open") {
      if (vDropdown.style.display === "block") {
        vDropdown.style.display = "none";
      } else {
        vDropdown.style.display = "block";
      }
    } else if (vDropdown && vToggle === "Close") {
      vDropdown.style.display = "none";
    }
  };

  const { activeProject } = useSelector((state) => state.user);
  const { selectedTable } = useSelector((state) => state.table);
  const projectId = activeProject?.project_UUID;
  const projectName = activeProject?.projectName;
  const utils = new Utils();
  const [tableData, setTableData] = useState([]);
  const [tableLAStatus, setTableLAStatus] = useState({});
  const [selectedTab] = useState(vTabIds.tables);
  const [displayAddTableModal, setDisplayAddTableModal] = useState(false);
  const [displaySuccess, setDisplaySuccess] = useState(false);
  const [displayFailure, setDisplayFailure] = useState(false);
  const [msgSuccess, setMsgSuccess] = useState("");
  const [msgFailure, setMsgFailure] = useState("");
  const [displayThresholdsModal, setDisplayThresholdsModal] = useState(false);
  const [addClicked, setAddClicked] = useState(true);
  const [selectedTableData, setSelectedTableData] = useState();
  const [showEditToast, setShowEditToast] = useState(false);
  const [showDeleteTableModal, setShowDeleteTableModal] = useState(false);
  const [showDeleteToast, setShowDeleteToast] = useState(false);
  const [isThresholdDisabled, setIsThresholdDisabled] = useState(false);
  const [deletedTable, setDeletedTable] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  /**
   * Navigate to home page
   */
  const navToHome = () => {
    dispatch(
      setSelectedTable({
        selectedTable: null,
      })
    );
    dispatch(
      setTables({
        tables: null,
      })
    );
    dispatch(
      setActiveProject({
        activeProject: null,
      })
    );
    dispatch(
      setUserProjects({
        projects: null,
      })
    );
    dispatch(
      setActiveProjectUserRole({
        activeProjectUserRole: null,
      })
    );
    navigate("/");
  };

  /**
   * Open Thresholds modal
   */
  const openThresholds = () => {
    setDisplayThresholdsModal(true);
  };

  /**
   * Close Thresholds modal
   */
  const closeThresholds = (vStatus, vError) => {
    setDisplayThresholdsModal(false);
    if (vStatus === "Success") {
      setDisplaySuccess(true);
      setMsgSuccess(
        "Global settings applied. You can review the Table settings for your tables."
      );
    } else {
      setDisplayFailure(true);
      setMsgFailure(vError || "Failed to update global threshold settings.");
    }
  };

  const tableService = new TableService();

  /**
   * Hook to fetch tables initially
   */
  useEffect(() => {
    if (projectId) {
      getTables(projectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, selectedTable]);
  /**
   * Function to get tables from backend
   * @param {*} vData
   */
  const getTables = async (vData) => {
    const vProjectId = vData || projectId;
    const vTables = await tableService.getTables(vProjectId);
    if (vTables[0]?.table_details) {
      vTables[0].table_details.map((table, i) =>
        setTableStatus(table, vTables[0].tableStatus)
      );
      vTables[0].table_details.forEach((table) => {
        table.ProjectLevelTable_status =
          vTables[0].ProjectLevelTable_status[table.id];
      });
      setTableData(vTables[0].table_details);
      setTableLAStatus(vTables[0].tableStatus);
      dispatch(
        setTables({
          tables: vTables[0].table_details,
        })
      );
    }
  };

  /**
   * Function to set Analysis in Progress flag as table level
   * @param {*} table
   * @param {*} tableStatus
   */
  const setTableStatus = async (table, tableStatus) => {
    if (tableStatus[table.id] === "In-Progress") {
      table.LAInProc = true;
      setIsThresholdDisabled(true);
    } else {
      table.LAInProc = false;
    }
  };

  /**
   * Function called when add table button clicked
   */
  const addTable = () => {
    setAddClicked(true);
    setDisplayAddTableModal(true);
  };
  /**
   * Function called when edit table button clicked
   */
  const onEditClick = (vTable) => {
    setSelectedTableData(vTable);
    setAddClicked(false);
    setDisplayAddTableModal(true);
  };
  /**
   * Function called when edit table button clicked
   */
  const onDeleteClick = (vTable) => {
    setDeletedTable(vTable);
    setShowDeleteTableModal(true);
  };
  /**
   * Function to close a Delete_relationship modal.
   */

  const handleCloseModal = () => {
    setShowDeleteTableModal(false);
  };

  /**
   * Function to delete a Delete_relationship .
   */

  const handleDelete = async () => {
    if (projectId && deletedTable) {
      const response = await tableService.deleteTable(
        projectId,
        deletedTable.id
      );
      if (response?.message === "Delete table executed successfully.") {
        clearAnalysisFromExcel(deletedTable);
        setShowDeleteTableModal(false);
        getTables(projectId);
        setShowDeleteToast(true);
      }
    }
  };

  const clearAnalysisFromExcel = (selectedTable) => {
    if (window.Excel) {
      window.Excel.run(async (context) => {
        const workbook = context.workbook;
        workbook.load("name");
        await context.sync();
        const workbookName = workbook.name;
        if (selectedTable?.metadata?.filename !== workbookName) {
          return;
        }

        const selectedRange = context.workbook.getSelectedRange();
        selectedRange.load(["worksheet/name"]);
        await context.sync();
        const sheetName = selectedRange.worksheet.name;
        if (selectedTable?.metadata?.sheetName !== sheetName) {
          return;
        }

        if (selectedTable?.metadata?.uploadRange) {
          const targetSheet = context.workbook.worksheets.getItem(sheetName);
          const vRange = selectedTable?.metadata?.uploadRange;

          const vCellToUpdate = await utils.getColumnLineCommentary(
            targetSheet,
            vRange,
            context
          );

          let pasteRange = `${vCellToUpdate.column}${vCellToUpdate.row}`;
          let cellToUpdate = targetSheet.getRange(pasteRange);
          cellToUpdate.load("values");
          await context.sync();

          // Clear Line Commentary Header
          cellToUpdate.values = [[""]];
          cellToUpdate.format.fill.color = "white";
          cellToUpdate.format.font.color = "black";
          await context.sync();

          // Clear Line Commentary Content
          let contentRange = `${vCellToUpdate.column}${vCellToUpdate.row + 1}:${
            vCellToUpdate.column
          }${vCellToUpdate.row + selectedTable.tableContent.length}`;
          let contentToUpdate = targetSheet.getRange(contentRange);
          contentToUpdate.load("values");
          await context.sync();
          contentToUpdate.values = Array(
            selectedTable.tableContent.length
          ).fill([""]);
          await context.sync();

          // Clear Table Commentary Header
          const vLineItemIndex = selectedTable.tableContent.length;
          let vTableCommentaryCell = vCellToUpdate;
          vTableCommentaryCell.row += vLineItemIndex + 2;
          pasteRange = `${vTableCommentaryCell.column}${vTableCommentaryCell.row}`;
          cellToUpdate = targetSheet.getRange(pasteRange);
          cellToUpdate.load("values");
          await context.sync();
          cellToUpdate.values = [[""]];
          cellToUpdate.format.fill.color = "white";
          cellToUpdate.format.font.color = "black";
          await context.sync();

          // Clear Table Commentary Content
          contentRange = `${vTableCommentaryCell.column}${
            vTableCommentaryCell.row + 1
          }:${vTableCommentaryCell.column}${
            vTableCommentaryCell.row + selectedTable.tableContent.length
          }`;
          contentToUpdate = targetSheet.getRange(contentRange);
          contentToUpdate.load("values");
          await context.sync();
          contentToUpdate.values = Array(
            selectedTable.tableContent.length
          ).fill([""]);
          await context.sync();
        }
      }).catch((error) => {
        console.error("Error: " + error);
      });
    }
  };

  /**
   * Function will trigger when add table modal will be clsed
   * @param {*} vStatus
   * @param {*} vError
   */
  const closeModal = (vStatus, vError, isAddClicked, isShowSuccessToast) => {
    setDisplayAddTableModal(false);
    if (vStatus === "Success") {
      getTables();
      if (isAddClicked === true) {
        setDisplaySuccess(true);
        setMsgSuccess("Table added successfully");
      } else if (isShowSuccessToast) {
        setDisplaySuccess(true);
        setMsgSuccess("Table edits updated");
      }
    } else {
      setDisplayFailure(true);
      if (isAddClicked) {
        setMsgFailure(vError || "Failed to add table.");
      } else {
        setMsgFailure("Failed to edit table.");
      }
    }
  };

  /**
   * Open selected table
   * @param {*} e
   */
  const onRowSelection = (e) => {
    if (e.column.colId !== "actions") {
      dispatch(
        setSelectedTable({
          selectedTable: e.data,
        })
      );
    }
  };

  /**
   * Close selected table
   * @param {*} vClose
   */
  const closeSelectedTable = (vClose) => {
    if (vClose) {
      dispatch(
        setSelectedTable({
          selectedTable: null,
        })
      );
    }
  };

  const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);

  const displayMembers = () => {
    setIsMembersModalOpen(true);
  };

  const openDocuments = () => {
    setIsDocumentsModalOpen(true);
  };

  return (
    <div className="projectContainer">
      <div className="projectDetailsBreadcrumb">
        <div className="motif-div-back-pro ellipsis-one-line">
          <MotifIconButton
            aria-label="Click here to go back to Project Dashboard"
            onClick={() => navToHome()}
            className="btnBack"
            data-testid="testIdBtnBack"
          >
            <img src={iconArrowBack} alt="Previous" />
          </MotifIconButton>
          <span className="projectName">{projectName}</span>
        </div>
        <div className="moti-btn-grp-div">
          <MotifButtonGroup>
            <MotifIconButton
              onClick={() => displayMembers()}
              variant="secondary"
              className="btnMember"
            >
              <MotifTooltip
                placement="bottom"
                trigger={<img src={iconMembers} alt="Members Icon" />}
                contentClassName="tooltipWithoutClose membersTooltip"
                allowClick={false}
                hideCloseButton={true}
              >
                Members
              </MotifTooltip>
            </MotifIconButton>
            <MotifIconButton
              onClick={() => openDocuments()}
              variant="secondary"
              className="btnDocument"
            >
              <MotifTooltip
                trigger={<img src={iconDocuments} alt="Document Icon" />}
                placement="left"
                contentClassName="tooltipWithoutClose documentsTooltip"
                allowClick={false}
                hideCloseButton={true}
              >
                Documents
              </MotifTooltip>
            </MotifIconButton>
          </MotifButtonGroup>
        </div>
      </div>
      {isMembersModalOpen && (
        <ProjectMembers
          isOpen={isMembersModalOpen}
          setOnclose={setIsMembersModalOpen}
        />
      )}
      {isDocumentsModalOpen && (
        <DocumentList
          isOpen={isDocumentsModalOpen}
          setOnclose={setIsDocumentsModalOpen}
        />
      )}
      <div className="projectContent">
        <div className="tabContentTables" id="idTabContentTables">
          {!selectedTable && (
            <MotifTable
              rowData={tableData}
              columnDefs={colDefs}
              suppressCellFocus
              suppressRowClickSelection
              suppressRowHoverHighlight
              className="tablesTable"
              overlayNoRowsTemplate={noRowsTemplate}
              enableSorting={false}
              onCellClicked={(e) => onRowSelection(e)}
            ></MotifTable>
          )}
          {selectedTable && (
            <TableDetails
              selectedTableforclear={selectedTableData}
              tableLAStat={tableLAStatus[selectedTable.id]}
              onCloseSelectedTable={closeSelectedTable}
              tables={tableData}
              isTableUnpublished={isUnpublishedTableType(selectedTable.tableType)}
            ></TableDetails>
          )}
          {showDeleteToast && (
            <MotifToast
              actionName=""
              onClose={() => {
                setShowDeleteToast(false);
              }}
              variant="info"
              className="notifyToast"
              icons={{ toast: <img src={iconWhiteCheckCircle} alt="" /> }}
            >
              Table has been deleted
            </MotifToast>
          )}
        </div>
        {(displaySuccess || displayFailure) && (
          <MotifToast
            actionName=""
            onClose={() => {
              if (displaySuccess) {
                setDisplaySuccess(false);
              } else {
                setDisplayFailure(false);
              }
            }}
            variant={displaySuccess ? "success" : "error"}
            className="notifyToast"
            icons={{
              toast: (
                <img
                  src={displaySuccess ? iconCheckCircle : iconError}
                  alt={displaySuccess ? "Success" : "failure"}
                />
              ),
            }}
          >
            {displaySuccess ? msgSuccess : msgFailure}
          </MotifToast>
        )}
        {showEditToast && (
          <MotifToast
            actionName=""
            onClose={() => {
              setShowEditToast(false);
            }}
            variant={"success"}
            className="notifyToast"
            icons={{ toast: <img src={iconCheckCircle} alt="Success" /> }}
          >
            Table edits updated.
          </MotifToast>
        )}
      </div>
      {selectedTab === vTabIds.tables && !selectedTable && (
        <div className="genericFooter">
          <div className="footerInner alignEnd">
            <MotifButton onClick={() => addTable()} className="btnAddTable">
              <img src={iconAdd} alt="Add Table" />
              <span className="textEllipsis">Add Table</span>
            </MotifButton>
            {displayAddTableModal && (
              <AddTable
                openModal={displayAddTableModal}
                setOpenModal={setDisplayAddTableModal}
                projectId={projectId}
                projectName={projectName}
                tables={tableData}
                closeModal={closeModal}
                isAddClicked={addClicked}
                selectedTable={selectedTableData}
                clearAnalysisFromExcel={clearAnalysisFromExcel}
              />
            )}
            {showDeleteTableModal && (
              <DeleteTableModal
                onClose={handleCloseModal}
                handleDelete={handleDelete}
              />
            )}
            <MotifButton
              onClick={() => openThresholds()}
              variant="secondary"
              className="btnThreshold"
              disabled={isThresholdDisabled}
            >
              <img
                src={iconThresholds}
                className={isThresholdDisabled ? "imgThreshold" : ""}
                alt="Thresholds"
              />
              <span className="textEllipsis">Thresholds</span>
            </MotifButton>
            {displayThresholdsModal && (
              <Thresholds
                openModal={displayThresholdsModal}
                setOpenModal={setDisplayThresholdsModal}
                closeModal={closeThresholds}
              />
            )}

            <MotifButton
              className="refreshButton"
              variant="secondary"
              id="btnRefreshProjectsList"
              onClick={() => {
                getTables();
              }}
              type="button"
            >
              <img
                className="refresh-icon"
                src={iconRefresh}
                alt="refresh tables icon"
              />
              <span className="textEllipsis">Refresh</span>
            </MotifButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetails;
